import React, { useEffect, useReducer, useRef } from "react";
import PinPop from "./PinPop";
import DirectionPop from "./DirectionPop";
import { useSelector } from "react-redux";
import { RootState } from "reducers/rootReducer";
import {
  GoogleMap,
  Marker,
  Circle,
  DirectionsRenderer,
  Polygon,

  Polyline,
  DrawingManager,
} from "@react-google-maps/api";
import { pushNotification } from "utils/notifications";
import { Button, Col, Row } from "antd";
import MediaMapMarker, { MediaMarkerData } from "./MediaMapMarker";
import MediaViewer from "./MediaViewer";
import { DEFAULT_MAP_CENTER, useBeamMapsScript } from "./mapsLoader";

const MeasureTool = require("measuretool-googlemaps-v3");

const getPaths = (polygon: any) => {
  var polygonBounds = polygon.getPath().getArray();
  var bounds = [];
  for (var i = 0; i < polygonBounds.length; i++) {
    var point = {
      lat: polygonBounds[i].lat(),
      lng: polygonBounds[i].lng(),
    };
    bounds.push(point);
  }
  bounds.push(bounds[0]);
  return bounds;
};

/*global google*/

const defaultLocation = DEFAULT_MAP_CENTER;
let timeout: NodeJS.Timeout;

const Map = (props) => {
  const [mapRef, setMapRef] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeMarker, setActiveMarker] = React.useState(null);
  // Only one media card is expanded at a time; the map owns that choice so two
  // cards can never overlap each other.
  const [activeMediaId, setActiveMediaId] = React.useState<string | null>(null);
  const [mediaViewerMarker, setMediaViewerMarker] =
    React.useState<MediaMarkerData | null>(null);
  const [directions, showDirection] = React.useState(null);
  const [selectDirectionPin, setSelectDirectionPin] = React.useState(null);
  const [drawingMode, setDrawingMode] = React.useState(null);
  const [polyline, setPolyline] = React.useState(false);
  const [insideBox, setInsideBox] = React.useState([]);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const gpsReportTrack = useRef<any>({ points: [] });

  const {
    active,
    setActive,
    onClick,
    showPanel,
    getPlaces,
    gpsMarkers,
    lockTracking,
    latestUpdateImei,
    settings,
    activePop,
    markers,
    polygons,
    circles,
    mode,
    currentLocation,
    mapType,
    toggleLock,
  } = props;

  const onToggleOpen = (marker) => {
    setIsOpen(!isOpen);
    setActiveMarker(marker);
  };

  const { isLoaded, loadError } = useBeamMapsScript();

  const { edit_place, gps } = useSelector(
    (state: RootState) => state.dashboard
  );

  const handleDirections = (destination: DestinationObj) => {
    const DirectionsService = new google.maps.DirectionsService();
    DirectionsService.route(
      {
        origin: selectDirectionPin,
        destination: new google.maps.LatLng(destination.lat, destination.lng),
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          showDirection(result);
          showPanel(true);
          setActive(true);
          setSelectDirectionPin(null);
        } else {
          console.error(`error fetching directions ${result}`);
        }
      }
    );
  };

  const existInsideBox = (p, bb) => {
    if (bb.ix <= p.x && p.x <= bb.ax && bb.iy <= p.y && p.y <= bb.ay) {
      return true;
    } else return false;
  };

  const onBoundsChanged = () => {
    if (mapRef) {
      let bounds = mapRef.getBounds();
      if (bounds) {
        let ne = bounds.getNorthEast();
        let sw = bounds.getSouthWest();
        const box = {
          center: mapRef.getCenter(),
          bounds: {
            minLat: sw.lat(),
            maxLat: ne.lat(),
            minLon: sw.lng(),
            maxLon: ne.lng(),
          },
        };

        let tempMarkers = { ...gpsMarkers };
        let insideBox = [];
        Object.keys(tempMarkers).map((key) => {
          tempMarkers?.[key]?.markers?.map((marker) => {
            const existInside = existInsideBox(
              { x: marker.lat, y: marker.lng },
              {
                ix: box.bounds.minLat,
                iy: box.bounds.minLon,
                ax: box.bounds.maxLat,
                ay: box.bounds.maxLon,
              }
            );
            if (existInside) {
              insideBox.push(marker.gpsTimestamp);
            }
          });
        });
        setPolyline(insideBox.length > 300);
        setInsideBox(insideBox);
        getPlaces(box);
      }
    }
  };

  const fitBounds = (gpsMarkersOnly = false) => {
    try {
      if (google) {
        const bounds = new google.maps.LatLngBounds();
        Object.keys(gpsMarkers).forEach((key) => {
          if (
            !lockTracking.checked ||
            (lockTracking.checked &&
              lockTracking.lock === latestUpdateImei.imei)
          ) {
            if (latestUpdateImei.mode || settings.breadcrumbs.length === 0) {
              let lastMarker = gpsMarkers[key].markers[0];
              if (activePop === "filters" && lastMarker) {
                bounds.extend({ lat: lastMarker.lat, lng: lastMarker.lng });
              } else if (mapRef && lastMarker) {
                mapRef.panTo(
                  new google.maps.LatLng({
                    lat: lastMarker.lat,
                    lng: lastMarker.lng,
                  })
                );
              }
            } else {
              gpsMarkers[key].markers.forEach((marker: any) => {
                bounds.extend({ lat: marker.lat, lng: marker.lng });
              });
            }
          }
        });

        if (!gpsMarkersOnly) {
          if (markers.length > 0 && markers[0].fitBounds) {
            markers.forEach((marker: any) => {
              bounds.extend({ lat: marker.lat, lng: marker.lng });
            });
          }

          polygons.forEach((polygon: any) => {
            polygon.paths.forEach((path: any) => {
              bounds.extend(path);
            });
          });
          circles.forEach((circle: any) => {
            bounds.union(
              new google.maps.Circle({
                center: { lat: circle.lat, lng: circle.lng },
                radius: circle.radius,
              }).getBounds()
            );
          });
        }
        if (bounds.isEmpty()) return;
        if (mapRef) {
          if (
            !gpsMarkersOnly &&
            markers.length === 1 &&
            polygons.length === 0 &&
            circles.length === 0
          ) {
            mapRef.panTo(
              new google.maps.LatLng({
                lat: markers[0].lat,
                lng: markers[0].lng,
              })
            );
          } else {
            mapRef.fitBounds(bounds);
          }
        }
      }
    } catch (e) {
      console.log("google failed");
    }
  };

  useEffect(() => {
    onBoundsChanged();
  }, [mapType]);

  useEffect(() => {
    if (currentLocation && mapRef) {
      mapRef.setZoom(16);
    }
  }, [currentLocation]);


  useEffect(()=>{
    console.log('gps markers', gpsMarkers);
  }, [gpsMarkers])

  useEffect(() => {
    if (edit_place) {
      if (edit_place.type === "marker") {
        const marker = markers.filter((marker) => {
          return marker.id === edit_place.id;
        });
        if (marker && marker.length === 1) {
          onToggleOpen({
            type: "marker",
            ...marker[0],
            mode: "edit",
          });
        }
      } else if (edit_place.type === "polygon") {
        const polygon = polygons.filter((polygon) => {
          return polygon.id === edit_place.id;
        });
        if (polygon && polygon.length === 1) {
          onToggleOpen({
            type: "polygon",
            ...polygon[0],
            mode: "edit",
          });
        }
      } else if (edit_place.type === "circle") {
        const circle = circles.filter((circle) => {
          return circle.id === edit_place.id;
        });
        if (circle && circle.length === 1) {
          onToggleOpen({
            type: "circle",
            ...circle[0],
            mode: "edit",
          });
        }
      }
    }
  }, [edit_place]);

  useEffect(() => {
    fitBounds();
  }, [markers, polygons, circles]);

  useEffect(() => {
    if (settings.track === "auto") fitBounds(activePop !== "filters");
    else {
      mapRef.setZoom(mapRef.getZoom());
    }
  }, [gpsMarkers]);

  useEffect(() => {
    if (gps?.points) {
      if (gps?.live && gps?.points?.length == 1) {
        const panorama = mapRef.getStreetView();
        if (panorama.getVisible()) {
          setTimeout(async () => {
            const sv = new google.maps.StreetViewService();
            const samples = [...gpsReportTrack?.current?.points].reverse();
            const panoLocation = await findPanorama(sv, samples, 0);
            if (panoLocation.location) {
              const nextLocation = samples?.[panoLocation.index]?.dataset;
              panorama.setPosition({
                lat: nextLocation?.locationGps?.lat,
                lng: nextLocation?.locationGps?.long,
              });
              panorama.setPov({
                heading: nextLocation?.headingGps,
                pitch: 0,
              });
            }
          });
        } else {
          console.log("panorama not visible");
        }
      }

      gpsReportTrack.current = gps;
    } else {
      gpsReportTrack.current = undefined;
    }
  }, [gps]);

  const getPanorama = (sv, searchLocation) => {
    return new Promise((resolve, reject) => {
      sv.getPanorama({ location: searchLocation, radius: 50 })
        .then(({ data }: google.maps.StreetViewResponse) => {
          const location = data.location!;
          resolve(location);
        })
        .catch((e) => {
          console.log("Street View data not found for this location.");
          resolve(undefined);
        });
    });
  };

  const findPanorama = async (sv, samples, startIndex) => {
    let startLocation;
    while (startIndex < samples.length) {
      startLocation = samples?.[startIndex]?.dataset?.locationGps;
      let location: any = await getPanorama(sv, {
        lat: startLocation.lat,
        lng: startLocation.long,
      });
      if (location) {
        return {
          index: startIndex,
          location,
        };
      }
      startIndex++;
    }
    return {
      index: startIndex,
      location: undefined,
    };
  };

  const loopPanorama = (panorama, samples, startIndex) => {
    if (startIndex === samples.length - 1) {
      panorama.setVisible(false);
      return;
    }
    const sv = new google.maps.StreetViewService();
    setTimeout(async () => {
      if (!panorama.getVisible()) return;
      const panoLocation = await findPanorama(sv, samples, startIndex);
      if (panoLocation.location) {
        const nextLocation = samples?.[panoLocation.index]?.dataset;

        panorama.setPosition({
          lat: nextLocation?.locationGps?.lat,
          lng: nextLocation?.locationGps?.long,
        });
        panorama.setPov({
          heading: nextLocation?.headingGps,
          pitch: 0,
        });
      } else {
        panorama.setVisible(false);
        return;
      }
      startIndex = panoLocation.index + 1;
      loopPanorama(panorama, samples, startIndex);
    }, 3000);
  };

  const onLoad = React.useCallback(function callback(ref) {
    const buttons = [
      ["Rotate Left", "rotate", 15, google.maps.ControlPosition.LEFT_CENTER],
      ["Rotate Right", "rotate", -20, google.maps.ControlPosition.RIGHT_CENTER],
      ["Tilt Down", "tilt", 15, google.maps.ControlPosition.TOP_CENTER],
      ["Tilt Up", "tilt", -20, google.maps.ControlPosition.BOTTOM_CENTER],
      ["Animate", "animate", 0, google.maps.ControlPosition.BOTTOM_CENTER],
    ];
    buttons.forEach(([text, mode, amount, position]) => {
      const controlUI = document.createElement("button");

      controlUI.classList.add("ui-button");
      controlUI.innerText = `${text}`;
      controlUI.addEventListener("click", async () => {

        switch (mode) {
          case "tilt":
            ref.setTilt(ref.getTilt() + amount);
            break;
          case "animate":
            if (gpsReportTrack?.current?.points?.length > 0) {
              let samples;

              if (!gpsReportTrack?.current?.live) {
                samples = [...gpsReportTrack?.current?.points].reverse();
              } else {
                samples = [gpsReportTrack?.current?.points?.[0]];
              }
              const panorama = ref.getStreetView();
              let startIndex = 0;
              let startLocation;
              const sv = new google.maps.StreetViewService();

              startLocation = await findPanorama(sv, samples, startIndex);
              if (startLocation.location) {
                panorama.setPano(startLocation.location.pano as string);
                panorama.setVisible(true);
              } else {
                pushNotification(
                  "Street view not found for this location",
                  "error",
                  "TOP_CENTER",
                  1000
                );
                return;
              }
              startIndex = startLocation.index;

              if (!gpsReportTrack?.current?.live) {
                loopPanorama(panorama, samples, startIndex + 1);
              }
            }
            break;
          case "rotate":
            ref.setHeading(ref.getHeading() + amount);
            break;
          default:
            break;
        }
      });
    });
    new MeasureTool(ref, {
      showSegmentLength: true,
      unit: MeasureTool.UnitTypeId.IMPERIAL, // or just use 'imperial'
    });
    setMapRef(ref);
  }, []);

  return (
    <React.Fragment>
      {isLoaded && (
        <GoogleMap
          center={currentLocation}
          zoom={14}
          options={{
            mapId: "bc10cdbc8ba6566d",
          }}
          onZoomChanged={() => {
            onBoundsChanged();
          }}
          onCenterChanged={() => {
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
              onBoundsChanged();
            }, 500);
          }}
          onClick={onClick}
          onLoad={onLoad}
          mapContainerStyle={{ height: "100%", width: "100%" }}
        >
          {markers.map((marker: any, idx: number) => {
            const isMedia =
              (marker.objectType === "media" ||
                marker.objectType === "medium") &&
              !!marker?.urls?.original?.url;

            // Media pins carry their own thumbnail and expand on click. The old
            // InfoWindow was always open, so a cluster of photos buried the map
            // underneath a wall of popups.
            if (isMedia) {
              const id = String(marker.id ?? idx);
              return (
                <MediaMapMarker
                  key={id}
                  marker={marker}
                  isActive={activeMediaId === id}
                  onSelect={() => setActiveMediaId(id)}
                  onClose={() => setActiveMediaId(null)}
                  onOpenMedia={(selected) => {
                    const mime = selected.data?.mimeType || "";
                    // Images, video and audio have in-app viewers; anything
                    // else is handed to the browser, as the map did before.
                    if (
                      mime.indexOf("image") !== -1 ||
                      mime.indexOf("video") !== -1 ||
                      mime.indexOf("audio") !== -1
                    ) {
                      setMediaViewerMarker(selected);
                    } else if (selected.urls?.original?.url) {
                      window.open(selected.urls.original.url, "_blank");
                    }
                  }}
                />
              );
            }

            return (
            <Marker
              key={idx}
              icon={
                marker.markerIcon
                  ? marker.markerIcon
                  : {
                    url: `/images/map-plain-icon-${marker.color}.svg`,
                    scaledSize: new google.maps.Size(30, 44),
                  }
              }
              position={{ lat: marker.lat, lng: marker.lng }}
              onClick={() => {
                if (selectDirectionPin) {
                  handleDirections(marker);
                } else {
                  onToggleOpen({
                    type: "marker",
                    ...marker,
                    mode: marker.filter ? marker.filter : "edit",
                  });
                }
              }}
            />
            );
          })}

          <Marker
            position={currentLocation}
            icon={{
              url: `/images/map-plain-icon-blue.svg`,
              scaledSize: new google.maps.Size(30, 44),
            }}
            onClick={
              () => {
                if (selectDirectionPin) {
                  handleDirections({
                    lat: currentLocation.lat,
                    lng: currentLocation.lng,
                  });
                } else {
                  onToggleOpen({
                    title: "current location",
                    lat: currentLocation.lat,
                    lng: currentLocation.lng,
                    type: "marker",
                  });
                }
              }
            }
          ></Marker>

          <DrawingManager
            drawingMode={drawingMode}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [
                  google.maps.drawing.OverlayType.MARKER,
                  google.maps.drawing.OverlayType.POLYGON,
                  google.maps.drawing.OverlayType.CIRCLE,
                ],
              },
              polygonOptions: {
                editable: true,
              },
              markerOptions: {
                icon: `/images/map-plain-icon-blue.svg`,
              },
            }}
            onCircleComplete={(circle: any) => {
              onToggleOpen({
                type: "circle",
                mode: "new",
                geometry: circle,
                title: "Circle",
                lat: circle.center.lat(),
                lng: circle.center.lng(),
                radius: circle.radius,
              });
              setDrawingMode(undefined);
            }}
            onPolygonComplete={(polygon: any) => {
              let paths = getPaths(polygon);
              onToggleOpen({
                type: "polygon",
                mode: "new",
                geometry: polygon,
                title: "Polygon",
                lat: paths[0].lat,
                lng: paths[0].lng,
              });
              setDrawingMode(undefined);
            }}
            onMarkerComplete={(marker: any) => {
              onToggleOpen({
                type: "marker",
                mode: "new",
                geometry: marker,
                title: "Marker",
                lat: marker.position.lat(),
                lng: marker.position.lng(),
              });
              setDrawingMode(undefined);
            }}
          />

          {polygons.map((polygon: any, idx: number) => (
            <Polygon
              path={polygon.paths}
              key={idx}
              editable={false}
              options={{
                strokeColor: polygon.color?.hex ?? "#000",
                fillColor: polygon.color?.hex ?? "#000",
              }}
              onClick={(click) => {
                onToggleOpen({ type: "polygon", ...polygon, mode: "edit" });
              }}
            />
          ))}

          {circles.map((circle: any, idx: number) => (
            <Circle
              key={idx}
              center={{
                lat: circle.lat,
                lng: circle.lng,
              }}
              radius={circle.radius}
              options={{
                strokeColor: circle.color?.hex ?? "#000",
                fillColor: circle.color?.hex ?? "#000",
              }}
              onClick={(click) => {
                onToggleOpen({ type: "circle", ...circle, mode: "edit" });
              }}
            />
          ))}

          {directions && active && (
            <DirectionsRenderer
              directions={directions}
              options={{
                markerOptions: { opacity: 1 },
                panel: document.getElementById("direction-panel"),
              }}
            />
          )}

          {Object.keys(gpsMarkers).map((key, idx) => (
            <React.Fragment key={`${new Date().toString()}`}>
              {!polyline &&
                gpsMarkers[key].markers
                  .slice(
                    0,
                    settings.breadcrumbs?.[0] === "Show breadcrumbs"
                      ? gpsMarkers[key].length
                      : 1 ?? 1
                  )
                  .filter((marker) => {
                    return insideBox.indexOf(marker.gpsTimestamp) !== -1;
                  })
                  .map((marker: any, idy: number) => (
                    <Marker
                      key={idy}
                      icon={marker.icon}
                      position={{ lat: marker.lat, lng: marker.lng }}
                      onRightClick={() => {
                        toggleLock(marker.imei);
                      }}
                      onClick={() => {

                        if (selectDirectionPin) {
                          handleDirections(marker);
                        } else {
                          onToggleOpen({
                            type: "marker",
                            ...marker,
                            mode: "edit",
                          });
                        }
                      }}
                    ></Marker>
                  ))}

              {polyline && (
                <Polyline
                  path={gpsMarkers[key].markers}
                  options={{
                    strokeColor: gpsMarkers[key].color,
                  }}
                />
              )}
            </React.Fragment>
          ))}

          {isOpen && activeMarker && (
            <PinPop
              setActivePop={onToggleOpen}
              currentLocation={currentLocation}
              marker={activeMarker}
              showDirection={(pin) => {
                setSelectDirectionPin(pin);
              }}
              setActive={setActive}
            />
          )}

          {selectDirectionPin && (
            <DirectionPop
              currentLocation={currentLocation}
              showDirections={(direction) => {
                handleDirections(direction);
              }}
              setActivePop={() => {
                setSelectDirectionPin(null);
              }}
            />
          )}

          {mapRef && (
            <Row className="map-rotation-icons">
              <Col span={8} className={""}>
                <Button
                  className="ui-button"
                  onClick={() => {
                    mapRef.setHeading(mapRef.getHeading() + 15);
                  }}
                >
                  <img className="action-icon" src="/images/left.png" />
                </Button>
              </Col>
              <Col className={"flex-column"} span={8}>
                <Button
                  className="ui-button"
                  onClick={() => {
                    mapRef.setTilt(mapRef.getTilt() - 15);
                  }}
                >
                  <img className="action-icon" src="/images/up.png" />
                </Button>
                <Button
                  className="ui-button blue"
                  onClick={async () => {
                    if (gpsReportTrack?.current?.points?.length > 0) {
                      let samples;
                      if (!gpsReportTrack?.current?.live) {
                        samples = [
                          ...gpsReportTrack?.current?.points,
                        ].reverse();
                      } else {
                        samples = [gpsReportTrack?.current?.points?.[0]];
                      }
                      const panorama = mapRef.getStreetView();
                      let startIndex = 0;
                      let startLocation;
                      const sv = new google.maps.StreetViewService();
                      startLocation = await findPanorama(
                        sv,
                        samples,
                        startIndex
                      );
                      if (startLocation.location) {
                        panorama.setPano(startLocation.location.pano as string);
                        panorama.setVisible(true);
                      } else {
                        pushNotification(
                          "Street view not found for this location",
                          "error",
                          "TOP_CENTER",
                          1000
                        );
                        return;
                      }
                      startIndex = startLocation.index;
                      if (!gpsReportTrack?.current?.live) {
                        loopPanorama(panorama, samples, startIndex + 1);
                      }
                    }
                  }}
                >
                  <img className="action-icon" src="/images/animate.png" />
                </Button>
                <Button
                  className="ui-button"
                  onClick={() => {
                    mapRef.setTilt(mapRef.getTilt() + 15);
                  }}
                >
                  <img className="action-icon" src="/images/down.png" />
                </Button>
              </Col>
              <Col className={""} span={8}>
                <Button
                  className="ui-button"
                  onClick={() => {
                    mapRef.setHeading(mapRef.getHeading() - 15);
                  }}
                >
                  <img className="action-icon" src="/images/right.png" />
                </Button>
              </Col>
            </Row>
          )}

          {/* VideoPlayerModal used to be opened from the media InfoWindow. That
              popup is gone and MediaViewer now handles video, audio and images
              on one path, so nothing could ever set videoModalOpen again. */}
          <MediaViewer
            marker={mediaViewerMarker}
            onClose={() => setMediaViewerMarker(null)}
          />
        </GoogleMap>
      )}
    </React.Fragment>
  );
};

export default Map;
